import * as React from "react";
import MUIDataTable, { MUIDataTableOptions } from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import { Box, Card, IconButton, Switch, Tooltip } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import { useNavigate } from "react-router-dom";
import axios from "../../api";
import SlideOverPanel from "components/SlideOverPanel";
import AddPage from "./AddPage";
import EditPage from "./EditPage";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { Edit as EditIcon } from "@mui/icons-material";
import { AxiosError } from "axios";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

interface PageData {
  page_id: number;
}

interface TableMeta {
  rowIndex: number;
  columnIndex: number;
  currentTableData: Array<{ index: number; data: unknown[] }>;
  tableData: PageData[];
}

const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

// Extracted components - OUTSIDE the main component
interface StatusCellProps {
  value: number;
  data: PageData;
  onStatusChange: (data: PageData, isActive: boolean) => void;
}

const StatusCell: React.FC<StatusCellProps> = ({
  value,
  data,
  onStatusChange,
}) => {
  return (
    <Switch
      checked={!!value}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        onStatusChange(data, event.target.checked)
      }
      color="primary"
    />
  );
};

interface DescriptionCellProps {
  value: string;
  parseContent: (content: string) => string;
}

const DescriptionCell: React.FC<DescriptionCellProps> = ({
  value,
  parseContent,
}) => {
  const content = parseContent(value);
  const displayValue =
    content?.length > 20 ? `${content.substring(0, 30)}...` : content;
  return (
    <Tooltip title={content}>
      <span>{displayValue}</span>
    </Tooltip>
  );
};

interface ActionsCellProps {
  data: PageData;
  onEdit: (pageId: number) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({ data, onEdit }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <IconButton aria-label="edit" onClick={() => onEdit(data?.page_id)}>
        <EditIcon />
      </IconButton>
    </Box>
  );
};

// Render helper functions - OUTSIDE the component
const renderStatusCell = (
  value: number,
  tableMeta: TableMeta,
  handleStatus: (data: PageData, isActive: boolean) => void
) => {
  const { currentTableData, rowIndex } = tableMeta;
  const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];
  return <StatusCell value={value} data={data} onStatusChange={handleStatus} />;
};

const renderDescriptionCell = (
  value: string,
  parseContent: (content: string) => string
) => {
  return <DescriptionCell value={value} parseContent={parseContent} />;
};

const renderActionsCell = (
  value: number,
  tableMeta: TableMeta,
  handleEdit: (pageId: number) => void
) => {
  const { currentTableData, rowIndex } = tableMeta;
  const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];
  return <ActionsCell data={data} onEdit={handleEdit} />;
};

const PagesList = () => {
  const navigate = useNavigate();
  const { fetchError, fetchSuccess } = useSnackbar();
  const [rows, setRows] = React.useState([]);
  const [addPanelOpen, setAddPanelOpen] = React.useState(false);
  const [editPanelOpen, setEditPanelOpen] = React.useState(false);
  const [selectedPageId, setSelectedPageId] = React.useState<number | null>(null);

  const parseContent = (content: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    return doc.body.textContent || "";
  };

  const handleStatus = async (data: PageData, isActive: boolean) => {
    try {
      await axios.put(`/pages/${data?.page_id}`, {
        is_active: isActive,
      });
      fetchData();
      fetchSuccess("Status updated successfully");
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };

  const handleEdit = (pageId: number) => {
    setSelectedPageId(pageId);
    setEditPanelOpen(true);
  };

  const columns = [
    { name: "page_name", label: "Name" },
    { name: "page_title", label: "Title" },
    {
      name: "is_active",
      label: "Status",
      options: {
        filter: false,
        customBodyRender: (value: number, tableMeta: TableMeta) =>
          renderStatusCell(value, tableMeta, handleStatus),
      },
    },
    {
      name: "page_description",
      label: "Description",
      options: {
        filter: false,
        customBodyRender: (value: string) =>
          renderDescriptionCell(value, parseContent),
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: number, tableMeta: TableMeta) =>
          renderActionsCell(value, tableMeta, handleEdit),
      },
    },
  ];

  const fetchData = React.useMemo(
    () => async () => {
      try {
        const response = await axios.get("/pages");
        setRows(response?.data?.list);
      } catch (error) {
        console.error("Error", error);
      }
    },
    []
  );

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <SoftButton
            variant="gradient"
            color="info"
            onClick={() => setAddPanelOpen(true)}
          >
            Add Page
          </SoftButton>
        </div>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-lifts"
        >
          <ThemeProvider theme={getMuiTheme}>
            {rows && (
              <MUIDataTable
                title={"Manage Pages"}
                data={rows}
                columns={columns}
                options={tableOptions}
              />
            )}
          </ThemeProvider>
        </SoftBox>
      </Card>
      <SlideOverPanel
        open={addPanelOpen}
        onClose={() => setAddPanelOpen(false)}
        title="Add Page"
        width={1000}
      >
        <AddPage
          embedded
          onSuccess={() => {
            setAddPanelOpen(false);
            fetchData();
          }}
          onCancel={() => setAddPanelOpen(false)}
        />
      </SlideOverPanel>
      <SlideOverPanel
        open={editPanelOpen}
        onClose={() => {
          setEditPanelOpen(false);
          setSelectedPageId(null);
        }}
        title="Edit Page"
        width={1000}
      >
        {selectedPageId != null && (
          <EditPage
            id={String(selectedPageId)}
            embedded
            onSuccess={() => {
              setEditPanelOpen(false);
              setSelectedPageId(null);
              fetchData();
            }}
            onCancel={() => {
              setEditPanelOpen(false);
              setSelectedPageId(null);
            }}
          />
        )}
      </SlideOverPanel>
    </DashboardLayout>
  );
};

export default PagesList;
